import { supabase } from '../lib/supabaseClient';
import { PROFILE_COLUMNS, type ProfileVisibleRow, type WorkerApplicationRow } from '../lib/db-types';
import type { CambiosDeFicha } from '../domain/fichaDeTrabajador';
import { logAction } from './audit';

export type ApplicationStatus = 'pendiente' | 'aprobado' | 'rechazado' | 'bloqueado';

/** Solicitudes de registro por estado, más recientes primero. */
export async function listApplications(status: ApplicationStatus): Promise<WorkerApplicationRow[]> {
  const { data, error } = await supabase
    .from('worker_applications')
    .select('*')
    .eq('status', status)
    .order('created_at', { ascending: false });
  if (error) throw new Error('No se pudieron cargar las solicitudes.');
  return (data ?? []) as WorkerApplicationRow[];
}

/** URL firmada del bucket privado `photos` (60 s por defecto). */
export async function signedFileUrl(path: string, seconds = 60): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from('photos').createSignedUrl(path, seconds);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/** Aprueba la solicitud vía Edge Function (crea usuario + copia documentos laborales). */
export async function approveApplication(
  id: string,
  email: string,
  tempPassword: string,
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('approve-worker', {
    body: { mode: 'single', application_id: id, email, temp_password: tempPassword },
  });
  if (error) {
    // eslint-disable-next-line no-console -- la causa real del fallo, que el mensaje no cabe
    console.error('approve-worker falló:', error);
    throw new Error(`No se pudo aprobar la solicitud: ${error.message}`);
  }
  const payload = data as { ok: boolean; error?: string; results?: { error?: string }[] };
  if (!payload?.ok) {
    const code = payload?.error ?? payload?.results?.[0]?.error ?? 'error_desconocido';
    throw new Error(translateApiError(code));
  }
  /**
   * `ok: true` con un error por dentro: la Edge Function crea al trabajador y DESPUÉS copia sus
   * documentos laborales en una sola sentencia. Si esa copia falla, el trabajador queda creado y
   * los documentos no se copian, pero la respuesta sigue diciendo `ok`. El admin veía «aprobado»
   * y el carné de sanidad no existía en ninguna parte.
   */
  const aviso = payload.results?.[0]?.error;
  if (aviso) {
    throw new Error(
      'El trabajador se creó, pero sus documentos laborales NO se copiaron: '
      + `${translateApiError(aviso)} Revísalo en su ficha antes de asignarlo.`,
    );
  }
}

/** Rechaza la solicitud con motivo (queda auditado). */
export async function rejectApplication(id: string, note: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('worker_applications')
    .update({
      status: 'rechazado',
      review_note: note,
      reviewed_by: userData.user?.id ?? null,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', id)
    .eq('status', 'pendiente')
    .select('id');
  /**
   * `.select('id')` y conteo de filas: un UPDATE que no calza —porque otra persona ya la aprobó o
   * rechazó, o porque la RLS lo filtró— devuelve **0 filas SIN error**. Sin esto la pantalla decía
   * «rechazada» y quedaba una entrada en la auditoría de un rechazo que nunca ocurrió.
   */
  if (error) throw new Error(`No se pudo rechazar la solicitud: ${error.message}`);
  if (!data || data.length === 0) {
    throw new Error('No se rechazó nada: la solicitud ya no está pendiente. Recarga la lista.');
  }
  await logAction('rechazar_solicitud', 'worker_applications', id, { note });
}

/** Bloquea el documento (lista negra): impide nuevos registros y bloquea el perfil si existe. */
export async function blockDocument(dni: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('block_document', { p_dni: dni, reason });
  if (error) throw new Error('No se pudo bloquear el documento.');
  await logAction('bloquear_documento', 'worker_applications', null, { dni, reason });
}

/** Traduce los códigos de error de la Edge Function a mensajes para el jefe de operaciones. */
export function translateApiError(code: string): string {
  const map: Record<string, string> = {
    correo_duplicado: 'Ese correo ya está en uso por otro usuario.',
    dni_duplicado: 'Ese documento ya tiene un usuario creado.',
    documento_bloqueado: 'Ese documento está bloqueado en la lista negra.',
    datos_incompletos: 'Faltan datos para crear el usuario.',
    solicitud_no_pendiente: 'La solicitud ya fue revisada por alguien más.',
    solicitud_no_encontrada: 'La solicitud ya no existe.',
    requiere_rol_admin: 'Solo el jefe de operaciones puede aprobar trabajadores.',
    no_autorizado: 'Tu sesión expiró. Vuelve a iniciar sesión.',
    dni_no_encontrado: 'No existe un trabajador con ese documento.',
    requiere_gestion_de_claves: 'No tienes permiso para cambiar claves.',
    no_puedes_cambiar_esa_clave:
      'Solo puedes cambiar la clave de trabajadores de campo. Para el resto, pídeselo al jefe de operaciones.',
    clave_muy_corta: 'La clave debe tener al menos 6 caracteres.',
  };
  return map[code] ?? `No se pudo completar la acción (${code}).`;
}

export type WorkerStatus = 'activo' | 'inactivo' | 'bloqueado';

/** Busca trabajadores por documento, nombres, apellidos o correo (vacío = primeros 50).
 *  PROFILE_COLUMNS y NO select('*'): la Task 4 revoca a `authenticated` el privilegio sobre
 *  bank/account_number/cci; los datos de pago se piden aparte con get_worker_payment_data(). */
export async function listWorkers(query: string): Promise<ProfileVisibleRow[]> {
  const q = query.trim();
  let request = supabase
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .order('last_names', { ascending: true })
    .limit(50);
  if (q.length > 0) {
    const like = `%${q}%`;
    request = request.or(
      `dni.ilike.${like},first_names.ilike.${like},last_names.ilike.${like},email.ilike.${like}`,
    );
  }
  const { data, error } = await request;
  if (error) throw new Error('No se pudieron cargar los trabajadores.');
  // `PROFILE_COLUMNS` no es un literal, así que supabase-js no puede inferir el shape del
  // select y tipa `data` como GenericStringError[]: hay que pasar por `unknown`.
  return (data ?? []) as unknown as ProfileVisibleRow[];
}

/** Reemplaza los roles del trabajador (valores válidos: admin, comercial, coordinador, recurso). */
export async function updateRoles(workerId: string, roles: string[]): Promise<void> {
  const { error } = await supabase.from('profiles').update({ roles }).eq('id', workerId);
  if (error) throw new Error(`${error.message} (no se pudieron guardar los roles)`);
  await logAction('actualizar_roles', 'profiles', workerId, { roles });
}

/** Cambia el estado del trabajador; `bloqueado` exige motivo y sella quién y cuándo. */
export async function setStatus(
  workerId: string,
  status: WorkerStatus,
  reason?: string,
): Promise<void> {
  if (status === 'bloqueado' && (!reason || reason.trim().length === 0)) {
    throw new Error('Para bloquear a un trabajador debes indicar el motivo.');
  }
  const { data: userData } = await supabase.auth.getUser();
  const patch =
    status === 'bloqueado'
      ? {
          status,
          block_reason: reason!.trim(),
          blocked_by: userData.user?.id ?? null,
          blocked_at: new Date().toISOString(),
        }
      : { status, block_reason: null, blocked_by: null, blocked_at: null };

  const { error } = await supabase.from('profiles').update(patch).eq('id', workerId);
  if (error) throw new Error('No se pudo cambiar el estado del trabajador.');
  await logAction('cambiar_estado_trabajador', 'profiles', workerId, {
    status,
    reason: reason?.trim() ?? null,
  });
}

/** Resetea la clave del trabajador vía Edge Function (modo reset_password). */
export async function resetPassword(dni: string, tempPassword: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('approve-worker', {
    body: { mode: 'reset_password', dni, temp_password: tempPassword },
  });
  if (error) {
    // eslint-disable-next-line no-console -- la causa real, que el mensaje corto no cabe
    console.error('reset_password falló:', error);
    throw new Error(`No se pudo cambiar la clave: ${error.message}`);
  }
  const payload = data as { ok: boolean; error?: string; results?: { error?: string }[] };
  if (!payload?.ok) {
    throw new Error(translateApiError(payload?.error ?? payload?.results?.[0]?.error ?? 'error'));
  }
  /**
   * El «vuelve a cambiarla» YA lo marcó el servidor (0085): se hace con la service key dentro de la
   * Edge Function y su fallo viaja en la respuesta. Antes se hacía acá con un UPDATE cuyo error se
   * ignoraba a propósito, y eso escondía un caso malo: si la RLS lo filtraba, la clave quedaba
   * cambiada y la persona NO estaba obligada a cambiarla — la clave que dictó el jefe se volvía su
   * clave definitiva y nadie se enteraba.
   */
  await logAction('resetear_clave', 'profiles', null, { dni });
}

export interface BulkWorkerInput {
  dni: string;
  first_names: string;
  last_names: string;
  phone: string;
  email: string;
  bank: string | null;
  account_number: string | null;
  cci: string | null;
  temp_password: string;
  /**
   * Los dos que siguen ya los aceptaba la función del servidor y NO estaban en este tipo, así que
   * desde la importación masiva no había forma de mandarlos. Se agregan para el alta manual (que
   * pide la fecha de nacimiento) y para poder dar de alta a alguien con carné de extranjería.
   *
   * Opcionales de verdad: la importación masiva sigue funcionando sin tocarlos, y el servidor los
   * trata como ausentes.
   */
  document_type?: 'DNI' | 'CE';
  birth_date?: string | null;
}

export interface BulkWorkerResult {
  dni: string;
  ok: boolean;
  error?: string;
}

/** Llama a la Edge Function approve-worker en modo bulk (máx. ~20 por llamada). */
export async function approveWorkersBulk(workers: BulkWorkerInput[]): Promise<BulkWorkerResult[]> {
  const { data, error } = await supabase.functions.invoke('approve-worker', {
    body: { mode: 'bulk', workers },
  });
  if (error) throw new Error(error.message);
  const payload = data as { ok: boolean; results: BulkWorkerResult[] };
  if (!payload || !Array.isArray(payload.results)) {
    throw new Error('Respuesta inesperada de approve-worker');
  }
  return payload.results;
}

export interface WorkerSearchResult {
  id: string;
  dni: string;
  first_names: string;
  last_names: string;
}

/**
 * Busca trabajadores activos con rol `recurso` por DNI o nombre (máx. 10 resultados).
 *
 * Va por la RPC `search_workers(q text)` (security definer, coordinador|admin) y NO por
 * `select` sobre `profiles`: la RLS de `profiles` solo permite al coordinador ver a los
 * recursos que YA tiene asignados, así que una consulta directa devolvería siempre vacío
 * justo en el caso de uso (asignar a alguien nuevo). Además la RPC no expone banco/cuenta/CCI.
 */
export async function searchWorkers(query: string): Promise<WorkerSearchResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase.rpc('search_workers', { q });
  if (error) throw new Error(error.message);
  return (data ?? []) as WorkerSearchResult[];
}

/* ---------------------------------------------------------------------------
 * Gestión de claves — permiso APARTE del rol admin (migración 0085)
 *
 * Decisión del usuario del 2026-09-04: solo su cuenta y la de Roberto Zumaeta pueden ver el estado
 * de acceso de la gente y cambiarle la clave, y un admin nuevo NO lo hereda.
 *
 * Nada de esto es un gate de pantalla: la base rechaza las tres llamadas si el permiso falta, y la
 * columna que lo concede no la puede escribir `authenticated` ni siendo admin.
 * ------------------------------------------------------------------------- */

/** ¿La sesión actual puede gestionar claves? */
export async function puedeGestionarClaves(): Promise<boolean> {
  const { data, error } = await supabase.rpc('puede_gestionar_claves');
  if (error) return false;
  return data === true;
}

/** Estado de acceso de un trabajador, para la columna «¿ya entró?». */
export interface EstadoDeClave {
  /** `true` = todavía usa la clave que le entregaron: nunca creó una propia. */
  sigueConClaveInicial: boolean;
  /** `null` = nunca ha iniciado sesión. */
  ultimoIngreso: string | null;
  cuentaCreada: string | null;
  /** Campaña más relevante, o `null` si nunca tuvo asignación. */
  campania: string | null;
  /** Tienda del PRÓXIMO turno. `null` si no tiene ninguno por delante. */
  tienda: string | null;
  /** Horario del próximo turno, como `09:00 a 18:00`. */
  horario: string | null;
  /** Fecha del próximo turno (`YYYY-MM-DD`). */
  proximoTurno: string | null;
}

/**
 * Estado de acceso de todos, indexado por id de trabajador.
 *
 * `last_sign_in_at` vive en `auth.users`, que el navegador no puede leer: sale de la RPC
 * `estado_de_claves`, que lo lee con privilegios del dueño y exige el permiso.
 */
export async function estadoDeClaves(): Promise<Map<string, EstadoDeClave>> {
  const { data, error } = await supabase.rpc('estado_de_claves');
  if (error) throw new Error(`No se pudo leer el estado de las claves: ${error.message}`);
  type Row = {
    worker_id: string;
    sigue_con_clave_inicial: boolean;
    ultimo_ingreso: string | null;
    cuenta_creada: string | null;
    campania: string | null;
    tienda: string | null;
    horario: string | null;
    proximo_turno: string | null;
  };
  return new Map(
    ((data ?? []) as unknown as Row[]).map((r) => [
      r.worker_id,
      {
        sigueConClaveInicial: r.sigue_con_clave_inicial === true,
        ultimoIngreso: r.ultimo_ingreso,
        cuentaCreada: r.cuenta_creada,
        campania: r.campania,
        tienda: r.tienda,
        horario: r.horario,
        proximoTurno: r.proximo_turno,
      },
    ]),
  );
}

/**
 * Guarda las correcciones de la ficha de un trabajador (pedido del 9-set).
 *
 * Recibe **solo lo que cambió** (`cambiosDeFicha`), no la ficha entera, y por eso:
 *
 * - Si no cambió nada, **no escribe ni audita** y devuelve `false`. Un rastro que dice «se corrigió
 *   la ficha» cuando alguien abrió la ventana y la cerró sin tocar nada es rastro falso, y ya pasó
 *   una vez en este sistema con las correcciones de cierre.
 * - El rastro guarda el **antes y el después de cada campo tocado**. Es lo que hace que la
 *   auditoría sirva: «se editó la ficha» sin los valores no permite reconstruir nada.
 *
 * Lo que esta función **no** puede cambiar, y no es un olvido: el documento y el tipo de documento
 * —la persona entra al sistema con `<documento>@campo.treid.pe`, así que cambiarlos acá la dejaría
 * sin poder entrar—, los roles y el estado, que tienen sus propios controles. La base lo hace
 * cumplir igual con el trigger `profiles_guard_privilegios`.
 */
export async function actualizarFichaTrabajador(
  workerId: string,
  cambios: CambiosDeFicha,
  antes: Record<string, unknown>,
): Promise<boolean> {
  const campos = Object.keys(cambios);
  if (campos.length === 0) return false;

  const { error } = await supabase.from('profiles').update(cambios).eq('id', workerId);
  if (error) throw new Error(translateApiError(error.code ?? '') || error.message);

  await logAction('editar_ficha_trabajador', 'profiles', workerId, {
    campos,
    antes: Object.fromEntries(campos.map((k) => [k, antes[k] ?? null])),
    despues: cambios as Record<string, unknown>,
  });
  return true;
}
